import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REGIONS } from "@/lib/data";

const CHIPS = ["Cobre", "HDPE", "Aceite industrial", "Pallets"];

export const Hero = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");

  const search = (e, term = q) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (term) p.set("q", term);
    if (region !== "all") p.set("region", region);
    navigate(`/marketplace?${p.toString()}`);
  };

  return (
    <section className="relative overflow-hidden bg-[#0a1f18] grain" data-testid="hero-section">
      <div className="absolute inset-0 grid-mask" />
      <div className="absolute -top-40 -right-32 w-[620px] h-[620px] rounded-full bg-[#00f2a1]/10 blur-3xl" />
      <svg className="orbit absolute -right-40 top-10 w-[640px] h-[640px] opacity-30 hidden lg:block" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="48" fill="none" stroke="#00f2a1" strokeWidth="0.15" strokeDasharray="1 2" />
        <circle cx="50" cy="50" r="36" fill="none" stroke="#00f2a1" strokeWidth="0.1" />
        <circle cx="98" cy="50" r="1.2" fill="#00f2a1" />
      </svg>
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <p className="rise inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#00f2a1] mb-6">
          <span className="w-2 h-2 rounded-full bg-[#00f2a1] live-dot" /> Simbiosis Industrial · Chile
        </p>
        <h1 className="rise text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white max-w-4xl" style={{ animationDelay: "80ms" }}>
          Transforma tus Mermas e Insumos Industriales en <span className="text-[#00f2a1]">Valor Circular</span>
        </h1>
        <p className="rise mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed" style={{ animationDelay: "160ms" }}>
          Conectamos empresas generadoras de scrap técnico con transformadores, recicladores y makers. Simbiosis industrial, trazabilidad y ahorro.
        </p>

        <form onSubmit={search} className="rise mt-10 max-w-3xl bg-[#0d1b1e]/80 backdrop-blur-xl border border-[#1b3d30] rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl" style={{ animationDelay: "240ms" }} data-testid="hero-search-form">
          <label className="sm:flex-1 flex items-center gap-3 px-4 h-12 rounded-xl bg-[#122a21]">
            <Search className="w-5 h-5 text-[#00f2a1] shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca material: Cobre, HDPE, Pallets..." aria-label="Buscar material"
              className="bg-transparent outline-none text-white placeholder:text-slate-500 w-full text-sm" data-testid="hero-search-input" />
          </label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="sm:w-56 h-12 rounded-xl bg-[#122a21] border-0 text-slate-200" data-testid="hero-region-select">
              <MapPin className="w-4 h-4 text-[#00f2a1]" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo Chile</SelectItem>
              {Object.keys(REGIONS).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" className="h-12 rounded-xl px-6 font-semibold hover:bg-[#00d890]" data-testid="hero-search-submit">Buscar</Button>
        </form>
        <div className="rise mt-4 flex flex-wrap gap-2" style={{ animationDelay: "300ms" }}>
          {CHIPS.map((c) => (
            <button key={c} onClick={() => search(null, c)} data-testid={`hero-chip-${c.toLowerCase().replace(/\s/g, "-")}`}
              className="text-xs px-3 py-1.5 rounded-full border border-[#1b3d30] text-slate-300 hover:border-[#00f2a1] hover:text-[#00f2a1] transition-colors">{c}</button>
          ))}
        </div>

        <div className="rise mt-12 flex flex-col sm:flex-row gap-4" style={{ animationDelay: "380ms" }}>
          <Button onClick={() => navigate("/publicar")} className="h-14 px-8 rounded-full text-base font-semibold hover:bg-[#00d890] hover:shadow-[0_0_28px_rgba(0,242,161,0.5)] transition-[background-color,box-shadow] duration-300" data-testid="hero-btn-sell">
            Quiero Vender / Liberar Stock <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate("/marketplace")} className="h-14 px-8 rounded-full text-base bg-transparent border-[#00f2a1]/40 text-white hover:bg-[#00f2a1]/10 hover:text-white" data-testid="hero-btn-buy">
            Buscar Materia Prima Secundaria
          </Button>
        </div>
      </div>
    </section>
  );
};
